import React from 'react'
import { Badge, Button, IconButton, Sidebar, Topbar, Tooltip, type NavItem } from '@/components/ds'
import { Ic } from '@/components/icons'
import { useTheme } from '@/lib/theme'
import { useAuth } from '@/lib/auth'
import { isConfigured } from '@/lib/supabase'
import { listCampaignStats, queueCounts } from '@/lib/db'
import { countLive } from '@shared/lifecycle'
import { getSendConfig } from '@/lib/api'
import { initials } from '@/lib/format'
import { Mascot, type MascotMood } from '@/components/Mascot'
import { LoginScreen } from '@/screens/LoginScreen'
import { SetupScreen } from '@/screens/SetupScreen'
import { DashboardScreen } from '@/screens/DashboardScreen'
import { QueueScreen } from '@/screens/QueueScreen'
import { CampaignsScreen } from '@/screens/CampaignsScreen'
import { TemplatesScreen } from '@/screens/TemplatesScreen'
import { LeadsScreen } from '@/screens/LeadsScreen'
import { IdentitiesScreen } from '@/screens/IdentitiesScreen'
import { LogsScreen } from '@/screens/LogsScreen'
import { AnalyticsScreen } from '@/screens/AnalyticsScreen'
import { SettingsScreen } from '@/screens/SettingsScreen'

export interface ScreenProps {
  narrow: boolean
  /** Lets a screen nudge the shell to re-read the queue badge. */
  onDataChange: () => void
  /** Jump to another screen without a page reload. */
  onNavigate: (page: string) => void
  /**
   * Take over the topbar (title + primary actions) while a sub-view is open.
   * `fullBleed` drops the main content padding for edge-to-edge layouts such as
   * the Templates split editor.
   */
  setHeader: (
    header: { title?: string; actions?: React.ReactNode; fullBleed?: boolean } | null,
  ) => void
}

const PAGES = {
  dashboard: { label: 'Dashboard', title: 'Dashboard', icon: 'dashboard', C: DashboardScreen },
  queue: { label: 'Queue', title: 'Send queue', icon: 'inbox', C: QueueScreen },
  campaigns: { label: 'Campaigns', title: 'Campaigns', icon: 'megaphone', C: CampaignsScreen },
  templates: { label: 'Templates', title: 'Templates', icon: 'template', C: TemplatesScreen },
  leads: { label: 'Leads', title: 'Leads', icon: 'users', C: LeadsScreen },
  identities: {
    label: 'Identities',
    title: 'Sending identities',
    icon: 'atSign',
    C: IdentitiesScreen,
  },
  logs: { label: 'Send logs', title: 'Send logs', icon: 'list', C: LogsScreen },
  analytics: { label: 'Analytics', title: 'Analytics', icon: 'chart', C: AnalyticsScreen },
  settings: { label: 'Settings', title: 'Settings', icon: 'sliders', C: SettingsScreen },
} as const

type PageId = keyof typeof PAGES

/**
 * The colourful backdrop, promoted app-wide from the sign-in screen.
 *
 * Fixed and viewport-clipped (.lm-bg has overflow:hidden), so a 500px blob
 * hanging off the right edge of a 375px phone can never widen the document.
 * Purely decorative — aria-hidden, no pointer events, frozen under
 * prefers-reduced-motion. Styles live in src/index.css.
 */
function AppBackdrop() {
  return (
    <div className="lm-bg" aria-hidden="true">
      <span className="lm-blob b1" />
      <span className="lm-blob b2" />
      <span className="lm-blob b3" />
      <span className="lm-blob b4" />
      <span className="lm-dots" />
    </div>
  )
}

function useMedia(query: string): boolean {
  const [matches, setMatches] = React.useState(() => window.matchMedia(query).matches)
  React.useEffect(() => {
    const mq = window.matchMedia(query)
    const onChange = () => setMatches(mq.matches)
    onChange()
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [query])
  return matches
}

export function App() {
  const { theme, toggle } = useTheme()
  const { session, email, loading, signOut } = useAuth()
  const narrow = useMedia('(max-width: 900px)')

  const [page, setPage] = React.useState<PageId>(() => {
    const saved = localStorage.getItem('lm-page')
    return saved && saved in PAGES ? (saved as PageId) : 'dashboard'
  })

  const [queuedCount, setQueuedCount] = React.useState(0)
  const [sendEnabled, setSendEnabled] = React.useState<boolean | null>(null)

  // A screen can put its own title + actions into the topbar (Templates' editor
  // does: "New template" with Show preview / Save). Cleared on navigation.
  const [screenHeader, setScreenHeader] = React.useState<{
    title?: string
    actions?: React.ReactNode
    fullBleed?: boolean
  } | null>(null)
  const setHeader = React.useCallback(
    (h: { title?: string; actions?: React.ReactNode; fullBleed?: boolean } | null) =>
      setScreenHeader(h),
    [],
  )
  React.useEffect(() => {
    setScreenHeader(null)
  }, [page])

  const QUIET = { mood: 'sad' as MascotMood, label: 'All quiet · no active campaigns' }
  const [mascot, setMascot] = React.useState<{ mood: MascotMood; label: string }>(QUIET)

  React.useEffect(() => {
    localStorage.setItem('lm-page', page)
  }, [page])

  const refreshBadges = React.useCallback(() => {
    if (!session) return
    // Badge = what is still waiting to go out (scheduled + held).
    queueCounts()
      .then((c) => setQueuedCount(c.scheduled + c.paused))
      .catch(() => setQueuedCount(0))

    // Mascot mood: exactly the campaigns the Campaigns list badges Active.
    // This used to ask its own question (rows waiting, or a delivery in the last
    // hour) while the badge read campaigns.status, so a drained campaign showed
    // "3/3 · Active" in the table beside "All quiet" in the sidebar. One call
    // now answers both.
    listCampaignStats()
      .then((stats) => {
        const live = countLive(stats)
        setMascot(
          live > 0
            ? {
                mood: 'happy',
                label: `Sending! ${live} campaign${live === 1 ? '' : 's'} live`,
              }
            : QUIET,
        )
      })
      .catch(() => setMascot(QUIET))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session])

  React.useEffect(() => {
    refreshBadges()
  }, [refreshBadges, page])

  React.useEffect(() => {
    if (!session) return
    getSendConfig()
      .then((c) => setSendEnabled(c.send_enabled))
      .catch(() => setSendEnabled(null))
  }, [session])

  const themeToggle = (
    <IconButton
      label={theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme'}
      onClick={toggle}
    >
      {theme === 'light' ? <Ic.moon size={16} /> : <Ic.sun size={16} />}
    </IconButton>
  )

  if (!isConfigured) return <SetupScreen />

  if (loading) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'grid',
          placeItems: 'center',
          color: 'var(--text-muted)',
          background: 'var(--bg-app)',
        }}
      >
        <Ic.spinner size={20} className="animate-spin" />
      </div>
    )
  }

  if (!session) return <LoginScreen themeToggle={themeToggle} />

  const items: NavItem[] = (Object.keys(PAGES) as PageId[]).map((id) => {
    const IconCmp = Ic[PAGES[id].icon]
    return { id, label: PAGES[id].label, icon: <IconCmp size={16} /> }
  })

  const Screen = PAGES[page].C

  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        overflow: 'hidden',
        // transparent, not --bg-app: the shell floats ON the backdrop below.
        background: 'transparent',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <AppBackdrop />
      {!narrow && (
        <Sidebar
          items={items}
          active={page}
          onSelect={(id) => setPage(id as PageId)}
          badgeCounts={{ queue: queuedCount || undefined }}
          middle={<Mascot mood={mascot.mood} label={mascot.label} />}
          footer={
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 10px',
                borderTop: '1px solid var(--border-default)',
              }}
            >
              <span
                style={{
                  width: 26,
                  height: 26,
                  flex: 'none',
                  borderRadius: '50%',
                  background: 'var(--accent-soft)',
                  color: 'var(--accent-text)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                {initials(email)}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: 'var(--text-primary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {email ?? 'Admin'}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Administrator</div>
              </div>
              <IconButton label="Sign out" onClick={() => void signOut()}>
                <Ic.logout size={16} />
              </IconButton>
            </div>
          }
        />
      )}

      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Topbar
          title={screenHeader?.title ?? PAGES[page].title}
          showMenu={narrow}
          items={items}
          active={page}
          onSelect={(id) => setPage(id as PageId)}
          actions={
            <>
              {screenHeader?.actions}
              {sendEnabled === false && (
                <Tooltip label="SEND_ENABLED is false — the Worker logs 'would send' instead of delivering">
                  <Badge status="pending" dot>
                    dry run
                  </Badge>
                </Tooltip>
              )}
              {sendEnabled === true && (
                <Tooltip label="SEND_ENABLED is true — real mail is going out">
                  <Badge status="sent" dot>
                    live
                  </Badge>
                </Tooltip>
              )}
              {themeToggle}
              {narrow && (
                <Button variant="ghost" size="sm" onClick={() => void signOut()}>
                  Sign out
                </Button>
              )}
            </>
          }
        />

        {/* Equal modest padding both sides, no centered column. A screen can ask
            for fullBleed (Templates' split editor) to fill the area edge to edge. */}
        <main
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: screenHeader?.fullBleed ? 'hidden' : 'auto',
            padding: screenHeader?.fullBleed ? 0 : narrow ? 12 : '18px 20px',
          }}
        >
          <Screen
            narrow={narrow}
            onDataChange={refreshBadges}
            onNavigate={(id) => id in PAGES && setPage(id as PageId)}
            setHeader={setHeader}
          />
        </main>
      </div>
    </div>
  )
}
